import { createApi } from '@reduxjs/toolkit/query/react'
import { PartnerPageMetric } from 'legacy/lut/configs/metrics'
import { IState } from 'legacy/lut/store'

import { FeatureType } from 'clickNgo/configs/feature'
import { ProfilingRequestSource } from 'clickNgo/configs/profiling/common'
import {
  OnboardingContactServiceMethods,
  OnboardingFail,
  OnboardingResponse,
  OnboardingResponseStatus,
  OnboardingServices,
} from 'clickNgo/configs/service/onboarding'
import { WebSocketStoreKeys } from 'clickNgo/configs/service/ws'
import { getJSendErrorMessages, transformToProcedurePayload } from 'clickNgo/helpers/api'
import { axiosProcedureQuery } from 'clickNgo/helpers/api/axiosProcedureQuery'
import { getAccessToken } from 'cng-common/helpers/access'
import { Nullable } from 'cng-common/models/utils'
import { NotificationController } from 'core-components/library/popup'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import Logger from 'core/utils/Logger'
import { measureRequest } from 'shared/lib/metrics'

import { ContactWorkflowStatus, IContact } from '../model/types'

import { mapContactFromContactAllData, mapContactState } from './mapping'

// ─── Types ────────────────────────────────────────────────────────────────────

type ActivateContactParams = {
  email: string
  targetPartnerId: string
  source?: ProfilingRequestSource
}

type ActivateContactResponse = {
  entryId375: Nullable<number>
  entryId379: Nullable<number>
  workflowStatus: ContactWorkflowStatus
  contactId: string
  partnerId: string
  email: string
  modifiedValues: string
}

type GetContactsByPartnerParams = {
  partnerId?: string
  topPartnerId?: string
  page?: number
  pageSize?: number
}

type GetContactsWithFlagsByPartnerParams = {
  partnerId?: string
  topPartnerId?: string
  isOnboarding?: boolean
  page?: number
  pageSize?: number
}

type GetContactsByPartnerResponse = {
  items: IContact[]
  total: number
}

type GetContactsWithFlagsByPartnerResponse = ReturnType<typeof mapContactState>

type GetContactsWithFlagsByPartnerQueryResult =
  | { data: GetContactsWithFlagsByPartnerResponse }
  | { error: unknown }

type GetContactsWithFlagsResponse = any[] | { contacts?: any[] }

type RelinkContactParams = {
  email: string
  targetPartnerId: string
  source: ProfilingRequestSource
  comment?: string
  onbRequestId?: number
}

type RelinkContactResponse = unknown

export enum ContactApiTags {
  ContactsByPartner = 'ContactsByPartner',
  ContactsWithFlagsByPartner = 'ContactsWithFlagsByPartner',
  ContactFlagsByContactIds = 'ContactFlagsByContactIds',
  UpdateContactFlags = 'UpdateContactFlags',
}

// ─── Shared WebSocket helper ──────────────────────────────────────────────────

type WsCallParams<TPayload extends Record<string, any> | undefined> = {
  action: OnboardingContactServiceMethods
  payload: TPayload
}

/**
 * Wraps every WebSocket call with unified error handling.
 * Returns RTK Query-compatible { data } | { error } result.
 */
async function wsContactCall<
  TData,
  TPayload extends Record<string, any> | undefined = Record<string, any>,
>({
  action,
  payload,
}: WsCallParams<TPayload>): Promise<
  { data: TData } | { error: { message: string; status: OnboardingResponseStatus } }
> {
  const token = getAccessToken()
  const wsClient = WebSocketStore.get(WebSocketStoreKeys.Onboarding)

  const response = (await wsClient.call({
    service: OnboardingServices.Contact,
    action,
    payload,
    token: token || undefined,
    withoutTokenRefresh: true,
    isAuth: true,
    returnError: true,
  })) as OnboardingResponse<OnboardingFail, TData>

  if (response.status === OnboardingResponseStatus.Error) {
    const { message, status } = response
    NotificationController.showError({ message })
    Logger.error({ message, status })
    return { error: { message, status } }
  }

  if (response.status === OnboardingResponseStatus.Fail) {
    const message = getJSendErrorMessages(response).join(', ')
    const status = response.status
    NotificationController.showError({ message })
    Logger.error({ message, status })
    return { error: { message, status } }
  }

  return { data: response.data }
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const contactApi = createApi({
  reducerPath: `${FeatureType.CngApi}/contactApi`,
  baseQuery: axiosProcedureQuery<any[]>(),
  tagTypes: Object.values(ContactApiTags),
  endpoints(build) {
    return {
      /**
       * Get contacts by partner
       * This query retrieves the contacts for a specific partner.
       */
      getContactsByPartner: build.query<GetContactsByPartnerResponse, GetContactsByPartnerParams>({
        transformResponse(response) {
          return {
            items: response.Data.map(mapContactFromContactAllData),
            total: response.Data.length,
          }
        },

        query({ page, pageSize, ...rest }) {
          return {
            url: `/webservice/197/sp/v4/spPartnerContactsByPartner/call`,
            method: 'POST',
            data: {
              ...transformToProcedurePayload(rest),
              page,
              pageSize,
            },
          }
        },
      }),

      getContactsWithFlagsByPartner: build.query<
        GetContactsWithFlagsByPartnerResponse,
        GetContactsWithFlagsByPartnerParams
      >({
        providesTags: [ContactApiTags.ContactsWithFlagsByPartner],
        queryFn: async (
          { isOnboarding, partnerId, topPartnerId },
          { getState }
        ): Promise<GetContactsWithFlagsByPartnerQueryResult> => {
          const result = await measureRequest(
            PartnerPageMetric.Contacts,
            selectUser(getState() as IState),
            () =>
              wsContactCall<GetContactsWithFlagsResponse>({
                action: OnboardingContactServiceMethods.GetContactsWithFlags,
                payload: {
                  ...(isOnboarding ? { partnerId, topPartnerId } : { topPartnerId }),
                  isOnboarding: Boolean(isOnboarding),
                },
              })
          )

          if ('error' in result) return result

          const contacts = Array.isArray(result.data) ? result.data : (result.data.contacts ?? [])

          return { data: mapContactState(contacts) }
        },
      }),

      /**
       * Activate contact
       * This mutation activates a contact.
       */
      activateContact: build.mutation<ActivateContactResponse, ActivateContactParams>({
        queryFn: (params) =>
          wsContactCall<ActivateContactResponse>({
            action: OnboardingContactServiceMethods.Activation,
            payload: {
              email: params.email,
              targetPartnerId: params.targetPartnerId,
              source: params.source ?? ProfilingRequestSource.ClickAndGoPortal,
            },
          }),
      }),

      /**
       * Relink contact
       * This mutation relinks a contact.
       */
      relinkContact: build.mutation<RelinkContactResponse, RelinkContactParams>({
        queryFn: (params) =>
          wsContactCall<RelinkContactResponse>({
            action: OnboardingContactServiceMethods.Relink,
            payload: params,
          }),
      }),

      updateIsPreferredContactFlag: build.mutation<
        void,
        { contactId: string; partnerId: string; isPreferredContact: boolean }
      >({
        queryFn: (payload) =>
          wsContactCall<void>({
            action: OnboardingContactServiceMethods.updateIsPreferredContactFlag,
            payload,
          }),
        invalidatesTags: [
          ContactApiTags.ContactsByPartner,
          ContactApiTags.ContactsWithFlagsByPartner,
        ],
      }),
    }
  },
})

export const {
  useActivateContactMutation,
  useRelinkContactMutation,
  useUpdateIsPreferredContactFlagMutation,
  useGetContactsByPartnerQuery,
  useGetContactsWithFlagsByPartnerQuery,
  useLazyGetContactsByPartnerQuery,
} = contactApi
